import React, { useRef, useState, useContext, useEffect, ReactNode } from 'react'

import styled from 'styled-components'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import { Container, Fonts, Icon } from 'components/base-ui'
import ProductList from 'components/ProductList'

import { AppContext } from 'contexts/AppProvider'
import { MenuCategoryType } from 'types'
import { ListProductsQuery } from 'graphQL/dinein/generate/operations'
import { ListProductsV2Query } from 'graphQL/hatostore/generate/operations'
import { useTranslation } from 'react-i18next'
import { filterSpecialCharacters } from 'helpers/string'

type ProductsType = ListProductsQuery['listProducts']['products'] | ListProductsV2Query['listProductsV2']['products']

interface SearchCategoriesAndProductsProps {
  show: boolean
  menuCategories: MenuCategoryType[]
  onClickCategoryMenu: (category: MenuCategoryType) => void
  onClickProduct: (product: ProductsType[number]) => void
  onBack: () => void
  listProducts: ProductsType
  vendor: any
  handleAddToCart: (product) => void
  membershipProgram: any
  onKeywordChange?: (keyword: string) => void
  children?: ReactNode
}

const SearchCategoriesAndProducts = ({
  show,
  menuCategories,
  onClickCategoryMenu,
  onClickProduct,
  listProducts,
  onBack,
  vendor,
  membershipProgram,
  handleAddToCart,
  onKeywordChange,
  children = null,
}: SearchCategoriesAndProductsProps) => {
  const { lang, isThaiLang } = useContext(AppContext)
  const { t } = useTranslation()

  const refScroll = useRef(null)
  const refInputSearch = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (refScroll.current) {
      if (show) {
        disableBodyScroll(refScroll.current)
      } else {
        enableBodyScroll(refScroll.current)
      }
    }
  }, [show, refScroll.current])

  useEffect(() => {
    // AutoFocus on inputSearch
    if (show && refInputSearch.current) {
      setTimeout(() => {
        refInputSearch.current && refInputSearch.current.focus()
      })
    }
  }, [show])

  // Clear value when close
  useEffect(() => {
    if (!show) {
      setSearch('')
    }
  }, [show])

  const handleOnKeyworkChange = (keyword: string) => {
    onKeywordChange && onKeywordChange(keyword)
    setSearch(keyword)
  }

  const textSearch = filterSpecialCharacters(search).toLowerCase()
  const _menuCategories = menuCategories.filter(
    (menu) => menu.nameTh.toLowerCase().includes(textSearch) || menu.nameEn.toLowerCase().includes(textSearch)
  )
  const _listProducts = listProducts?.filter(
    (product) => product.nameTh.toLowerCase().includes(textSearch) || product.nameEn.toLowerCase().includes(textSearch)
  )

  return (
    <StoreLocatorContainer>
      <ScrollWrapper ref={refScroll}>
        <Container style={{ paddingTop: '20px' }}>
          <HeaderWrapper>
            <BtnBack onClick={onBack}>
              <IconArrowLeft name="arrow-left" />
            </BtnBack>
            <SearchInputWrapper>
              <IconSearch name="search" />
              <SearchInput
                ref={refInputSearch}
                type="text"
                placeholder={t('searchProducts')}
                value={search}
                onChange={(e) => handleOnKeyworkChange(e.target.value)}
                data-testid="search-category-product"
              />
              <ButtonClearSearch show={search !== ''} onClick={() => handleOnKeyworkChange('')}>
                <IconClearSearch name="plus-2" />
              </ButtonClearSearch>
            </SearchInputWrapper>
          </HeaderWrapper>

          {_menuCategories.length > 0 && search !== '' && (
            <CategoriesContainer data-testid="category-item">
              <Fonts.SmallTitleBold>{t('chooseCategoryMenuHeader')}</Fonts.SmallTitleBold>
              <MenuCategories>
                {_menuCategories.map((menu, i) => {
                  return (
                    <MenuCategory key={i} onClick={() => onClickCategoryMenu(menu)}>
                      <Fonts.BaseTitleBold>{isThaiLang ? menu.nameTh : menu.nameEn}</Fonts.BaseTitleBold>
                    </MenuCategory>
                  )
                })}
              </MenuCategories>
            </CategoriesContainer>
          )}

          {_listProducts.length > 0 && search !== '' && (
            <ProductsContainer>
              <ProductList
                data={_listProducts}
                vendor={vendor}
                lang={lang}
                addToCart={true}
                onAddToCart={(product) => handleAddToCart(product)}
                membershipProgram={membershipProgram}
                onClickProduct={onClickProduct}
                data-testid="menu-item"
              />
            </ProductsContainer>
          )}
          {children}
        </Container>
      </ScrollWrapper>
    </StoreLocatorContainer>
  )
}

export default SearchCategoriesAndProducts

const StoreLocatorContainer = styled.div`
  height: 100%;
  padding-bottom: 10px;
`

const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 16px;
`

const HeaderWrapper = styled.div`
  display: flex;
`

const BtnBack = styled.div`
  display: flex;
  align-items: center;
  padding-right: 10px;
  margin-right: 5px;
`

const IconArrowLeft = styled(Icon)`
  font-size: 18px;
  color: ${(p) => p.theme.colors.primary};
`

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`

const IconSearch = styled(Icon)`
  position: absolute;
  top: 50%;
  left: 20px;
  font-size: 14px;
  color: ${(p) => p.theme.colors.darkGrey};
  transform: translate(0, -50%);
`

const SearchInput = styled.input`
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid ${(p) => p.theme.colors.lightGrey};
  border-radius: 10px;
  width: 100%;
  min-height: 47px;
  font-family: ${Fonts.family.brand};
  font-weight: ${Fonts.weight.semiBold};
  font-size: 15px;
  padding-left: 42px;
  padding-right: 35px;
  color: ${(p) => p.theme.colors.base};

  &::-webkit-input-placeholder {
    color: ${(props) => props.theme.colors.darkGrey};
  }

  &:-ms-input-placeholder {
    color: ${(props) => props.theme.colors.darkGrey};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.darkGrey};
  }

  &:-webkit-autofill {
    background-color: red;
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
  }
`

const ButtonClearSearch = styled.div<{ show: boolean }>`
  width: 40px;
  height: 40px;
  position: absolute;
  top: 50%;
  margin-top: -20px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: hidden;
  transition: 0.3s;
  transform: translate(0, 15px);
  opacity: 0;

  ${(p) =>
    p.show &&
    `
    visibility: visible;
    transform: translate(0, 0);
    opacity: 1;
  `}
`

const IconClearSearch = styled(Icon)`
  font-size: 11px;
  transform: rotate(45deg);
  color: ${(props) => props.theme.colors.darkGrey};
`

const CategoriesContainer = styled.div`
  padding-top: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${(p) => p.theme.colors.lightGrey};
`

const MenuCategories = styled.div`
  margin-top: 9px;
`

const MenuCategory = styled.div`
  position: relative;
  color: ${(p) => p.theme.colors.darkGrey};
  padding: 4px 0;

  &::after {
    content: '\\e96c';
    font-family: 'icons-indiedish' !important;
    position: absolute;
    font-size: 14px;
    top: 50%;
    right: 0;
    color: ${(p) => p.theme.colors.primary};
    transform: translate(0, -50%);
  }
`

const ProductsContainer = styled.div`
  padding-bottom: 56px;
`
